<template>
  <div ref="autocompleteContainer" :class="['autocomplete-container', { 'icon-only': shouldRenderIconOnly }]">
    <div v-if="error" class="search-error">
      {{ error.message }}
    </div>
    <div v-if="isLoading && !shouldRenderIconOnly" class="search-loading">Loading search...</div>
  </div>
</template>

<script>
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import '@algolia/autocomplete-theme-classic'
import algoliasearch from 'algoliasearch/lite'
import nynowLogo from '@/assets/img/nynow/nynow-logo.png'

const HITS_PER_PAGE = 3
const INIT_TRADE_SHOWS_HITS_PER_PAGE = 1
const TRADE_SHOWS_HITS_PER_PAGE_WITH_QUERY = 2
const SUGGESTIONS_PER_PAGE = 5
const RECENT_SEARCHES_LIMIT = 3
const MAX_ACTIVE_TRADE_SHOWS_COUNT = 2

export default {
  name: 'AlgoliaAutocomplete',
  props: {
    navigationEventIds: {
      type: Array,
      default: () => []
    },
    shouldRenderIconOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchClient: null,
      isPanelOpen: false,
      error: null,
      isLoading: true,
      autocompleteInstance: null,
      lastNavigatedRoute: null
    }
  },
  watch: {
    isLoading(newValue) {
      if (!newValue && !this.error) {
        this.initializeAutocomplete()
      }
    },
    $route() {
      const { name: currentRouteName, params: currentRouteParams } = this.$route
      const { name: lastNavigatedRouteName, params: lastNavigatedRouteParams } = this.lastNavigatedRoute || {}
      if (
        currentRouteName !== lastNavigatedRouteName ||
        JSON.stringify(currentRouteParams) !== JSON.stringify(lastNavigatedRouteParams)
      ) {
        if (this.autocompleteInstance) {
          this.autocompleteInstance.setQuery('')
        }
      }
    }
  },
  async created() {
    await this.initializeSearchClient()
  },
  async mounted() {
    if (this.error || this.isLoading) return

    this.initializeAutocomplete()
  },
  methods: {
    goToCollection(id, name) {
      this.lastNavigatedRoute = {
        name: 'productsFromNavigation',
        params: {
          navItemName: name,
          navItemId: id
        }
      }
      this.$router.push(this.lastNavigatedRoute)
    },
    goToSearchPageWithParam(keyword) {
      this.lastNavigatedRoute = {
        name: 'search',
        query: {
          keyword,
          page: 1
        }
      }
      this.$router.push(this.lastNavigatedRoute)
    },
    // Create recent searches plugin with proper context
    createRecentSearchesPlugin() {
      return createLocalStorageRecentSearchesPlugin({
        key: 'autocomplete_recent_searches',
        limit: RECENT_SEARCHES_LIMIT,
        transformSource: ({ source, state }) => {
          const query = source.getItemInputValue({ item: state.query })
          return {
            ...source,
            templates: {
              ...source.templates,
              header({ items }) {
                return items.length > 0 || query ? 'Recent' : null
              }
            },
            onSelect: ({ item }) => {
              this.goToSearchPageWithParam(item.label)
            }
          }
        }
      })
    },

    // Create suggestions plugin with proper context
    createSuggestionsPlugin(searchClient) {
      return createQuerySuggestionsPlugin({
        searchClient,
        indexName: `${this.$env.getOrigin()}_products_query_suggestions`,
        getSearchParams({ state }) {
          return {
            hitsPerPage: state.query ? SUGGESTIONS_PER_PAGE : 3
          }
        },
        transformSource: ({ source }) => {
          return {
            ...source,
            templates: {
              ...source.templates,
              header({ items }) {
                return items.length > 0 ? 'Trending' : null
              }
            },
            onSelect: ({ item }) => {
              this.goToSearchPageWithParam(item.query)
            }
          }
        },
        categoryAttribute: [`${this.$env.getOrigin()}_brands`, `${this.$env.getOrigin()}_categories`],
        itemsWithCategories: SUGGESTIONS_PER_PAGE,
        categoriesPerItem: SUGGESTIONS_PER_PAGE
      })
    },
    async initializeSearchClient() {
      try {
        this.isLoading = true
        this.searchClient = algoliasearch(this.$env.algolia().appId, this.$env.algolia().token)

        await this.searchClient.search([
          {
            indexName: `${this.$env.getOrigin()}_brands`,
            params: { hitsPerPage: 0 }
          }
        ])

        this.error = null
      } catch (err) {
        this.error = err
        console.error('Failed to initialize Algolia client:', err)
      } finally {
        this.isLoading = false
      }
    },

    initializeAutocomplete() {
      if (!this.searchClient) return

      const recentSearchesPlugin = this.createRecentSearchesPlugin()
      const querySuggestionsPlugin = this.createSuggestionsPlugin(this.searchClient)

      this.autocompleteInstance = autocomplete({
        container: this.$refs.autocompleteContainer,
        placeholder: 'Search',
        detachedMediaQuery: '',
        plugins: [recentSearchesPlugin, querySuggestionsPlugin],
        insights: true,
        openOnFocus: true,
        onStateChange: ({ state }) => {
          this.isPanelOpen = state.isOpen
          this.$emit('panel-state-change', state.isOpen)
          document.body.style.overflow = state.isOpen ? 'hidden' : 'auto'
        },
        onSubmit: ({ state }) => {
          this.goToSearchPageWithParam(state.query)
        },
        getSources: ({ query }) => {
          return [
            // categories source
            query
              ? {
                  sourceId: 'categories',
                  getItems: () => {
                    return getAlgoliaResults({
                      searchClient: this.searchClient,
                      queries: [
                        {
                          indexName: `${this.$env.getOrigin()}_categories`,
                          params: {
                            query,
                            hitsPerPage: HITS_PER_PAGE,
                            attributesToRetrieve: ['name'],
                            attributesToHighlight: ['name'],
                            highlightPostTag: '</em>',
                            highlightPreTag: '<em>',
                            snippetEllipsisText: '…'
                          }
                        }
                      ]
                    })
                  },
                  getItemInputValue: ({ item }) => item.name,
                  templates: {
                    header: () => {
                      return 'Categories'
                    },
                    item: ({ item, html }) => {
                      return html`<div class="aa-ItemContent">
                        <span>${item.name}</span>
                        <i class="icon icon-chevron-right" style="width: 25px; height: 25px;"></i>
                      </div>`
                    }
                  },
                  onSelect: ({ item }) => {
                    this.goToCollection(item.objectID, item.name)
                  }
                }
              : null,
            // brands Source
            {
              sourceId: 'brands',
              getItems: () => {
                return getAlgoliaResults({
                  searchClient: this.searchClient,
                  queries: [
                    {
                      indexName: `${this.$env.getOrigin()}_brands`,
                      params: {
                        query,
                        facetFilters: ['is_published', 'status'],
                        filters: 'is_published:true AND (status:APPROVED OR status:REGISTERED)',
                        hitsPerPage: HITS_PER_PAGE,
                        attributesToHighlight: ['brand_name'],
                        snippetEllipsisText: '…',
                        highlightPostTag: '</em>'
                      }
                    }
                  ]
                })
              },
              getItemInputValue: ({ item }) => item.brand_name,
              templates: {
                header: () => {
                  return 'Brands'
                },
                item({ item, html }) {
                  return html`<div class="aa-ItemContent">
                    ${item.thumbnail_image_url
                      ? html`<img
                          src="${item.thumbnail_image_url}"
                          alt="${item.brand_name || 'Brand image'}"
                          class="aa-ItemImage"
                          loading="lazy"
                        />`
                      : ''}
                    <div class="aa-ItemText">
                      <span class="aa-ItemTitle">${item.brand_name}</span>
                      <div class="aa-ItemMinimumOrder">$${item.minimum_order_value} minimum</div>
                    </div>
                  </div>`
                }
              },
              onSelect: ({ item }) => {
                this.lastNavigatedRoute = {
                  name: 'brand-bio',
                  params: {
                    brandUrl: item.vanity_url_name
                  }
                }
                this.$router.push(this.lastNavigatedRoute)
              }
            },
            {
              sourceId: 'tradeShows',
              getItems: () => {
                return getAlgoliaResults({
                  searchClient: this.searchClient,
                  queries: [
                    {
                      indexName: `${this.$env.getOrigin()}_trade_shows`,
                      params: {
                        query,
                        facetFilters: this.navigationEventIds?.length
                          ? [this.navigationEventIds.map((id) => `event_id:${id}`)]
                          : [],
                        filters: this.navigationEventIds?.length
                          ? `event_id:${this.navigationEventIds?.join(' OR event_id:')}`
                          : '',
                        hitsPerPage:
                          this.navigationEventIds?.length === MAX_ACTIVE_TRADE_SHOWS_COUNT
                            ? TRADE_SHOWS_HITS_PER_PAGE_WITH_QUERY
                            : INIT_TRADE_SHOWS_HITS_PER_PAGE,
                        attributesToRetrieve: ['*']
                      }
                    }
                  ]
                })
              },
              getItemInputValue: ({ item }) => item.name,
              templates: {
                header: () => {
                  return 'Featured Trade Shows'
                },
                item({ html }) {
                  return html`<div class="aa-ItemContent">
                    <img src=${nynowLogo} style="width: 132px; height: 40px;" />
                  </div>`
                }
              },
              onSelect: ({ item }) => {
                this.lastNavigatedRoute = {
                  name: 'exhibitor-directory',
                  params: { slug: item?.brand ? this.$utils.flatten(item.brand) : 'nynow' }
                }
                this.$router.push(this.lastNavigatedRoute)
              }
            }
          ]
        }
      })
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/sass/base/_vars.scss';
@import '@/assets/sass/base/_mixins.scss';
@import '@/assets/sass/base/_typo.scss';

// Common values
$search-input-height: 40px;
$search-border-radius: 4px;
$search-padding-horizontal: 10px;

@mixin search-border {
  border: 1px solid $color-black;
  border-radius: $search-border-radius;
}

.autocomplete-container {
  margin: 0 auto;
  max-width: 800px;
}

.icon-only {
  display: flex;
  align-items: center;
  justify-content: center;

  .aa-DetachedSearchButton {
    padding: 0;
    background-color: transparent;
    border: none;

    &:focus {
      outline: none;
      box-shadow: none;
    }

    &Placeholder,
    &Query {
      display: none;
    }

    &Icon {
      margin-left: 0;
      order: 1;
      background-color: transparent;

      svg {
        display: none;
      }

      &::after {
        content: '';
        display: inline-block;
        width: 120px;
        height: 45px;
        background-color: $color-black;
        mask: url('@/assets/icons/search.svg') no-repeat center;
        mask-size: contain;
        -webkit-mask: url('@/assets/icons/search.svg') no-repeat center;
        -webkit-mask-size: contain;
      }

      &:hover::after {
        cursor: pointer;
        opacity: 0.6;
      }
    }
  }
}

.aa-DetachedContainer--modal {
  max-width: 800px;
  margin: 0 auto;

  @include respond-above('xlarge') {
    max-width: 835px;
  }
}

.aa-Panel {
  z-index: 1;
  &Layout {
    padding: 0;
  }
}

.aa-SourceHeader {
  @extend %subhead-2;
  text-transform: uppercase;
  padding-left: 20px;
}

.aa-DetachedContainer {
  padding: 0;
}

.aa-DetachedFormContainer {
  border-bottom: none;
  margin-bottom: 5px;
  padding: 20px;
}

.aa-DetachedSearchButton {
  @extend %body-copy-1;
  @include search-border;
  display: flex;
  padding: 0 0 0 $search-padding-horizontal;
  background-color: $color-grey-2;
  width: 100%;

  &Icon {
    margin-left: auto;
    width: $search-input-height;
    order: 3;
    background-color: $color-black;

    svg {
      fill: $color-white;
    }
  }

  &Placeholder,
  &Query {
    @extend %subhead-1;
    color: $color-black;
  }

  &Placeholder {
    order: 1;
  }

  &Query {
    order: 2;
  }
}

.aa-Form {
  @include search-border;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: $search-padding-horizontal;
  width: 100%;
  background-color: $color-grey-2;
  outline: none;
  color: $color-black;
  box-shadow: none;
  text-align: right;

  &:focus-within,
  &:active {
    border-color: $color-black;
    box-shadow: none;
  }
}

.aa-Input {
  @extend %body-copy-1;
  color: $color-black;

  &::placeholder {
    color: $color-black;
  }
}

.aa-InputWrapperPrefix {
  order: 5;
}

.aa-SubmitButton {
  width: $search-input-height;
  border: 1px solid $color-black;
  background-color: $color-black;
}

.aa-Label {
  button svg {
    fill: $color-white;
  }
}

.aa-DetachedCancelButton {
  @extend %subhead-1;

  &:hover {
    outline: none;
    box-shadow: none;
    opacity: 0.7;
  }
}

.search-error {
  color: $color-error;
  padding: 10px;
  border: 1px solid $color-error;
  border-radius: $search-border-radius;
  margin-bottom: 10px;
  text-align: center;
}

.search-loading {
  color: $color-black;
  padding: 10px;
  text-align: center;
}

.aa-DetachedOverlay {
  background-color: rgba(0, 0, 0, 0.7);
}

.aa-Item {
  @extend %body-copy-1;

  &Content {
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

.aa-ItemActionButton {
  &:nth-child(2) {
    display: none;
  }
}

section[data-autocomplete-source-id='categories'] .aa-List {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 5px;
  padding-inline: 20px;

  &.aa-List .aa-Item {
    display: flex;
    gap: 5px;
    border: 1px solid $color-grey-3;
    border-radius: 100px;
    padding: 4px 8px 4px 16px;

    &:hover {
      background-color: $color-green;
    }
    &[aria-selected='true'] {
      background-color: $color-green;
    }
  }
}

section[data-autocomplete-source-id='brands'] .aa-List {
  display: flex;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
  flex-direction: row;
  margin-top: 5px;
  padding-inline: 20px;

  @include respond-to('xsmall') {
    align-items: start;
    gap: 5px;
    flex-direction: column;
  }
  @include respond-to('small') {
    align-items: start;
    gap: 5px;
    flex-direction: column;
  }

  @include respond-to('medium') {
    align-items: start;
    gap: 5px;
    flex-direction: column;
  }

  .aa-Item {
    padding: 10px 5px;

    &Content {
      display: flex;

      align-items: center;
      gap: 10px;
      width: 100%;

      .aa-ItemImage {
        width: 80px;
        height: 80px;
        border-radius: 4px;
        flex-shrink: 0;
      }

      .aa-ItemText {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;

        .aa-ItemTitle {
          @extend %body-copy-1;
          color: $color-black;
          margin-bottom: 5px;
        }

        .aa-ItemMinimumOrder {
          @extend %body-copy-1;
          font-size: 12px;
          color: $color-grey-4;
        }
      }
    }

    &:hover {
      background-color: transparent;
      opacity: 0.5;
    }

    &[aria-selected='true'] {
      background-color: transparent;
    }
  }
}

section[data-autocomplete-source-id='recentSearchesPlugin'] {
  .aa-Item {
    @extend %body-copy-1;
    padding-inline: 20px;

    &Content {
      display: flex;
      align-items: center;
      gap: 5px;
    }

    &:hover {
      background-color: $color-green;
    }
    &[aria-selected='true'] {
      background-color: $color-green;
    }
  }
}

section[data-autocomplete-source-id='recentSearchesPlugin'] .aa-ItemActions {
  .aa-ItemActionButton {
    font-size: 12px;
    svg {
      display: none;
    }

    &::after {
      content: '';
      display: inline-block;
      width: 25px;
      height: 25px;
      background-color: $color-black;
      mask: url('@/assets/icons/close.svg') no-repeat center;
      mask-size: contain;
      -webkit-mask: url('@/assets/icons/close.svg') no-repeat center;
      -webkit-mask-size: contain;
      transition: transform 0.2s ease;
    }

    &:hover::after {
      opacity: 0.5;
    }
  }
}

section[data-autocomplete-source-id='querySuggestionsPlugin'] .aa-List {
  .aa-Item {
    padding-inline: 20px;

    &:hover {
      background-color: $color-green;
    }
    &[aria-selected='true'] {
      background-color: $color-green;
    }
  }
}

section[data-autocomplete-source-id='querySuggestionsPlugin'] .aa-ItemActions {
  display: none;
}

section[data-autocomplete-source-id='tradeShows'] .aa-List {
  display: flex;
  flex-wrap: wrap;
  padding-inline: 17px;

  @include respond-to('xsmall') {
    align-items: start;
    gap: 5px;
    flex-direction: column;
  }
  @include respond-to('small') {
    align-items: start;
    gap: 5px;
    flex-direction: column;
  }
  @include respond-to('medium') {
    align-items: start;
    gap: 5px;
    flex-direction: column;
  }

  .aa-Item {
    flex: 1;
    padding: 10px 5px;
    width: 100%;

    &Content {
      display: flex;
      background-color: #f3ece5;
      align-items: center;
      justify-content: center;
      gap: 10px;
      max-width: 362px;
      padding: 20px;
      width: 100%;
    }

    &:hover {
      background-color: transparent;
      opacity: 0.5;
    }

    &[aria-selected='true'] {
      background-color: transparent;
    }
  }
}
</style>
